import {
  Box,
  BoxProps,
  Col,
  css,
  DSIconAttachment16,
  DSIconDoc,
  DSIconEffort,
  DSIconGhost,
  DSIconMilestone,
  DSIconMisc,
  DSIconPrioA,
  DSIconPrioB,
  DSIconPrioC,
  DSIconSelectionClose,
  DSIconSelectionEmpty,
  DSIconSprint,
  DSShortcutKey,
  Row,
  Text,
} from "@cdx/ds";
import {type ReactNode, lazy, forwardRef, MouseEventHandler, useRef, useEffect} from "react";
import {animated, useSpring} from "react-spring";
import dsStyles from "@cdx/ds/css/index.css";
import {
  IconGateClosed,
  TooltipForChild,
  cx,
  formatMsAsMin,
  getMetaKeyLabel,
  springConfigs,
} from "@cdx/common";
import Avatar from "../../components/Avatar";
import UnMarkedown from "../../components/Markdown/UnMarkedown";
import {enrichCardProjectTags} from "../../components/Markdown/CardTags";
import {getChildCardInfo, getParentCard} from "../workflows/workflow-utils";
import Emoji from "../../components/Markdown/Emoji";
import {DeckPickerImage} from "../card-container/DSDeckPicker";
import {getTotalTimeForCard} from "../time-tracking/time-tracking-utils";
import {
  HandPositionHint,
  HourGlassTooltip,
  shouldShowZoneHint,
  trackedTimeToProgress,
  TrackingTooltip,
  ZoneHint,
} from "../../components/Card/CardBottomHints";
import {DueDateInfo, getDueDateInfo} from "../due-date/due-date-utils";
import {DueDateIcon, DueDateTooltipContent} from "../due-date/DueDateCardHintIcon";
import {UpvoteTooltip, getUpvoteInfo} from "../upvotes/upvote-utils";
import {PotionIcon} from "../../components/Card/PotionIcon";
import {getCardHandInfo} from "../hand-queue/hand-queue-utils";
import Ui from "../../components/ui2";
import uiClasses from "@cdx/common/xui/ui.css";
import {DependencyOverlay} from "../dependencies/DependencyArea";
import {DisplayFile as RawDisplayFile} from "../card-container/sidebars/attachments";
import {ConversationHint, getConvoPreviewInfo} from "../conversations/ConversationHint";
import {CardPanelBag} from "./card-panel-utils";
import {getStatusForCard} from "../../lib/card-status-utils";
import {usePropHover} from "../../components/Card/useHoverShortcuts";
import {tableViewStyles as styles} from "./card-panel.css";
import {CardPropChangeOverlayForChild} from "../../components/RichTextarea/Lexical/CardPropChangeForChild";
import ProgressCircle from "../../components/ProgressCircle";
import cardStyles from "../../components/Card/card.css";
import {getMilestoneState, setMsThemeColor} from "../milestones/milestone-utils";
import {IconWithMilestoneTooltipById} from "../../pages/milestones/MilestoneTooltip";
import {Milestone, MilestoneId} from "../../cdx-models/Milestone";
import {Root} from "../../cdx-models/Root";
import {Card, CardId} from "../../cdx-models/Card";
import {ProjectTag} from "../../cdx-models/ProjectTag";
import {Deck} from "../../cdx-models/Deck";
import {ThemedSprintIconWithTooltipAndMetaClick, getSprintState} from "../milestones/sprint-utils";
import {Sprint, SprintId} from "../../cdx-models/Sprint";
import {BeastPill, getBeastLevel} from "../../components/Card/Beast";
import {AddedDuringSprintPill, getIsNewUntil} from "../../components/Card/AddedDuringSprint";
import {getCardOrderInfo} from "./ZoneComponents";
import Markdown from "../../components/Markdown";
import {StopWatchIconWithProgress} from "../card-container/AnimatedStopWatchIcon";
import {Project} from "../../cdx-models/Project";
import {Link} from "react-router-dom";

const DisplayFile = RawDisplayFile as any;

type CellProps = {children?: ReactNode; fixed: number; flex?: number} & Omit<BoxProps, "flex">;

const Cell = forwardRef<HTMLDivElement, CellProps>(({children, fixed, flex, ...props}, ref) => (
  <Box
    px="8px"
    py="8px"
    overflow="hidden"
    {...(props as any)}
    style={{flex: `${fixed + 16}px ${flex ?? 0} 1`}}
    relative
    className={styles.cell.withFadeOut}
    ref={ref}
  >
    {children}
  </Box>
));

export const TableCell = Cell;

const iconByPrio = {
  a: DSIconPrioA,
  b: DSIconPrioB,
  c: DSIconPrioC,
};

const DSPrio = ({priority}: {priority: "a" | "b" | "c"}) => {
  const Icon = iconByPrio[priority];
  if (!Icon) return null;
  return <Icon size={16} className={dsStyles.color.secondary} />;
};

export const PrioCell = (props: {
  card: Card;
  canModify: boolean | null;
  cardContainerKey: string;
}) => {
  const {card, canModify, cardContainerKey} = props;
  const prio = card.$meta.get("priority", null);
  const hoverEvents = usePropHover("priority");

  const tooltip = canModify && (
    <Box px="8px" py="4px">
      Set Priority <DSShortcutKey>p</DSShortcutKey> or <DSShortcutKey>0</DSShortcutKey> -{" "}
      <DSShortcutKey>3</DSShortcutKey>
    </Box>
  );

  return (
    <Cell fixed={16} {...(canModify && hoverEvents)}>
      <CardPropChangeOverlayForChild
        cardId={card.cardId as CardId}
        cardContainerKey={`${cardContainerKey}-priority`}
        disabled={!canModify}
        getChildProps={(open) => ({hidden: open ? true : undefined})}
        initialTab="priority"
      >
        <TooltipForChild tooltip={tooltip} delayed>
          {prio ? (
            <Box>
              <DSPrio priority={prio} />
            </Box>
          ) : (
            <Box
              color="secondary"
              className={cx(
                uiClasses.hideElement,
                uiClasses.hideElementTargetOpacities["0.25"],
                uiClasses.hideElementInitialOpacities["0.15"]
              )}
            >
              <DSIconPrioA size={16} />
            </Box>
          )}
        </TooltipForChild>
      </CardPropChangeOverlayForChild>
    </Cell>
  );
};

export const EffortContent = (props: {
  card: Card;
  canModify: boolean | null;
  cardContainerKey: string;
  root: Root;
}) => {
  const {card, canModify, cardContainerKey, root} = props;
  const childCardInfo = getChildCardInfo(card, root.account);
  const shownEffort = childCardInfo ? childCardInfo.effort : card.$meta.get("effort", null);
  const hoverEvents = usePropHover("effort");
  const tooltip = canModify && (
    <Box px="8px" py="4px">
      Set Effort <DSShortcutKey>e</DSShortcutKey> or <DSShortcutKey>0</DSShortcutKey> -{" "}
      <DSShortcutKey>9</DSShortcutKey>
    </Box>
  );

  if (shownEffort === null)
    return (
      <Cell fixed={32} {...(childCardInfo || !canModify ? null : hoverEvents)}>
        <CardPropChangeOverlayForChild
          cardId={card.cardId as CardId}
          cardContainerKey={`${cardContainerKey}-effort`}
          disabled={!canModify}
          getChildProps={(open) => ({hidden: open ? true : undefined})}
          initialTab="effort"
        >
          <TooltipForChild tooltip={tooltip} delayed>
            <DSIconEffort
              size={16}
              className={cx(
                dsStyles.color.secondary,
                uiClasses.hideElement,
                uiClasses.hideElementTargetOpacities["0.25"],
                uiClasses.hideElementInitialOpacities["0.15"]
              )}
            />
          </TooltipForChild>
        </CardPropChangeOverlayForChild>
      </Cell>
    );
  return (
    <Cell
      fixed={32}
      display="flex"
      sp="4px"
      align="center"
      {...(childCardInfo || !canModify ? null : hoverEvents)}
    >
      <CardPropChangeOverlayForChild
        cardId={card.cardId as CardId}
        cardContainerKey={`${cardContainerKey}-effort`}
        disabled={!canModify}
        getChildProps={(open) => ({hidden: open ? true : undefined})}
        initialTab="effort"
      >
        <TooltipForChild tooltip={tooltip} delayed>
          <DSIconEffort size={16} className={dsStyles.color.secondary} />
        </TooltipForChild>
      </CardPropChangeOverlayForChild>
      <Box bold color="secondary" size={shownEffort > 9 ? 12 : 14} lineHeight="16px">
        {shownEffort}
      </Box>
    </Cell>
  );
};

export const EffortCell = ({
  card,
  canModify,
  cardContainerKey,
  root,
}: {
  card: Card;
  canModify: boolean | null;
  cardContainerKey: string;
  root: Root;
}) =>
  card.isDoc ? (
    <Cell fixed={32}>
      <DSIconDoc size={16} className={dsStyles.color.secondary} />
    </Cell>
  ) : (
    <EffortContent
      card={card}
      canModify={canModify}
      cardContainerKey={cardContainerKey}
      root={root}
    />
  );

export const showOwnerCell = (card: Card) => card.$meta.get("assignee", null) !== null;

export const OwnerCell = (props: {
  card: Card;
  canModify: boolean | null;
  cardContainerKey: string;
}) => {
  const {card, canModify, cardContainerKey} = props;
  const tooltip = canModify && (
    <Box px="8px" py="4px">
      Set Owner <DSShortcutKey>o</DSShortcutKey>
    </Box>
  );

  return (
    <CardPropChangeOverlayForChild
      cardId={card.cardId as CardId}
      cardContainerKey={`${cardContainerKey}-owner`}
      disabled={!canModify}
      initialTab="owner"
      getChildProps={(open) => ({hidden: open ? true : undefined})}
    >
      <TooltipForChild tooltip={tooltip} delayed>
        <Cell fixed={20}>
          {card.assignee && <Avatar {...({size: 20, user: card.assignee} as any)} />}
        </Cell>
      </TooltipForChild>
    </CardPropChangeOverlayForChild>
  );
};

const LazyWorkflowHoverInfo = lazy(
  () => import(/* webpackChunkName: "WorkflowOverInfo" */ "../workflows/WorkflowHoverInfo")
);

const ParentCardBadge = ({card}: {card: Card}) => {
  const emojis = enrichCardProjectTags(card)
    .map((t: any) => t.emoji)
    .filter(Boolean)
    .join("");
  const shownTitle = emojis ? `${emojis} ${card.title}` : card.title;

  return (
    <TooltipForChild
      tooltip={() => <LazyWorkflowHoverInfo card={card} hideParent={false} />}
      placement="right"
      bg="gray800"
      distanceFromAnchor={10}
      delayed
    >
      <UnMarkedown
        className={css({
          size: 14,
          colorTheme: "gray750",
          bg: "foreground",
          color: "primary",
          rounded: 4,
          whiteSpace: "nowrap",
          textOverflow: "clip",
          px: "6px",
          py: "4px",
          weight: "bold",
          lineHeight: "14px",
          minWidth: "48px",
        })}
        style={{marginTop: -2, marginBottom: -2, flexShrink: "0.001"}}
      >
        {shownTitle}
      </UnMarkedown>
    </TooltipForChild>
  );
};
const IsHeroCard = ({childCardInfo: info, card}: {childCardInfo: any | null; card: Card}) => {
  return (
    <TooltipForChild
      tooltip={() => <LazyWorkflowHoverInfo card={card} hideParent />}
      placement="right"
      bg="gray800"
      distanceFromAnchor={10}
      delayed
    >
      <Row
        size={14}
        bold
        className={styles.parentBadge.container}
        rounded={4}
        px="6px"
        py="4px"
        sp="8px"
        lineHeight="14px"
        align="center"
        style={{marginTop: -3, marginBottom: -3}}
      >
        <UnMarkedown as="div" projectId={card.deck?.project.id} className={dsStyles.flex.none}>
          {card.title}
        </UnMarkedown>
        <Row
          rounded={4}
          height="4px"
          overflow="hidden"
          relative
          top="1px"
          className={cardStyles.childCardInfo.sectionCount.md}
        >
          {["done", "review", "blocked", "started", "rest"].map((label) =>
            (info[label] as any[]).map((effort, idx) => (
              <div
                key={`${label}-${idx}`}
                style={{minWidth: `${(effort || 0) + 4}px`}}
                className={cx(
                  cardStyles.childCardInfoBarTile.base,
                  label !== "rest" && (cardStyles as any).childCardInfoBarTile[label]
                )}
              />
            ))
          )}
        </Row>
      </Row>
    </TooltipForChild>
  );
};

export const TitleCell = ({card, root}: {card: Card; root: Root}) => {
  const parentCard = getParentCard(card);
  const childCardInfo = getChildCardInfo(card, root.account);

  return (
    <Cell
      fixed={128}
      flex={2}
      whiteSpace="nowrap"
      textOverflow="clip"
      size={14}
      lineHeight="16px"
      display="flex"
      sp="4px"
      align="baseline"
    >
      {childCardInfo ? (
        <IsHeroCard childCardInfo={childCardInfo} card={card} />
      ) : (
        <>
          {parentCard && parentCard.$meta.isLoaded && <ParentCardBadge card={parentCard} />}
          <UnMarkedown
            as="span"
            projectId={card.deck?.project.id}
            style={{flexShrink: "1000"}}
            className={css({
              color: "primary",
              whiteSpace: "nowrap",
              textOverflow: "clip",
              minWidth: "32px",
            })}
          >
            {card.title}
          </UnMarkedown>
        </>
      )}
    </Cell>
  );
};

export const showCheckListCell = (card: Card) =>
  card.$meta.get("checkboxStats", {total: 0, checked: 0}).total > 0;

export const CheckListCell = ({card}: {card: Card}) => {
  const {total, checked} = card.$meta.get("checkboxStats", {total: 0, checked: 0});
  if (!total) return <Cell fixed={48} />;
  return (
    <Cell fixed={52} display="flex" sp="4px" align="center">
      <Box height="16px">
        <ProgressCircle progress={checked / total} />
      </Box>
      <Box color="secondary" size={11} whiteSpace="nowrap">
        {checked !== total ? `${checked} / ${total}` : `${checked}`}
      </Box>
    </Cell>
  );
};

const DSProjectTag = ({
  tagObj,
}: {
  tagObj:
    | ProjectTag
    | {$meta?: undefined; description: string; emoji: string; color: string; tag: string};
}) => {
  const desc = tagObj.$meta ? tagObj.$meta.get("description", null) : tagObj.description;
  const getPrefix = () => {
    const emoji = tagObj.$meta ? tagObj.$meta.get("emoji", null) : tagObj.emoji;
    const color = tagObj.$meta ? tagObj.$meta.get("color", null) : tagObj.color;
    if (emoji)
      return (
        <Box as="span" size={11} pr="2px">
          <Emoji inline>{emoji}</Emoji>
        </Box>
      );
    if (color) {
      return <span style={{color}}>#</span>;
    }
    return (
      <Box as="span" color="secondary" bold size={12}>
        #
      </Box>
    );
  };
  const content = (
    <Box
      key={tagObj.tag}
      flex="none"
      colorTheme="gray50"
      bg="foreground"
      rounded={4}
      px="4px"
      color="secondary"
      bold
      size={12}
    >
      {getPrefix()}
      {tagObj.tag}
    </Box>
  );
  return desc ? (
    <TooltipForChild
      bg="gray700"
      tooltip={() => (
        <Markdown size="sm" variant="onDark" className={css({pa: "8px", maxWidth: "200px"})}>
          {desc}
        </Markdown>
      )}
    >
      {content}
    </TooltipForChild>
  ) : (
    content
  );
};

const Tags = ({card}: {card: Card}) => {
  const enrichedTags = enrichCardProjectTags(card);
  if (!enrichedTags.length) return null;
  return enrichedTags.map((tagObj: any) =>
    tagObj.isDeprecated ? (
      <Box key={tagObj.tag} flex="none" color="secondary" bold size={12}>
        #{tagObj.tag}
      </Box>
    ) : (
      <DSProjectTag tagObj={tagObj} key={tagObj.tag} />
    )
  );
};

export const showTagsCell = (card: Card) => card.$meta.get("masterTags", []).length > 0;

export const TagCell = ({card}: {card: Card}) => (
  <Cell fixed={72} flex={0.15} display="flex" sp="4px">
    <Tags card={card} />
  </Cell>
);

const MsContent = (props: {
  ms?: Milestone | null;
  root: Root;
  cardId: CardId;
  cardContainerKey: string;
  canModify: boolean | null;
  zoneInfo?: string | null;
}) => {
  const {ms, root, cardId, cardContainerKey, canModify, zoneInfo} = props;

  if (process.env.REACT_APP_MODE === "open") return null;

  const msColor = ms?.$meta.get("color", null) ?? null;
  const milestoneState = ms && getMilestoneState(root.account, ms);
  return ms ? (
    <>
      <IconWithMilestoneTooltipById
        milestoneId={ms.id as MilestoneId}
        color={msColor}
        milestoneState={milestoneState}
        propChangeInfo={canModify && {cardId, cardContainerKey}}
        openMsOnMetaClick={process.env.REACT_APP_MODE !== "open" ? {cardId} : undefined}
        tooltip={
          process.env.REACT_APP_MODE !== "open" ? (
            <>
              <DSShortcutKey>m</DSShortcutKey> to change, {getMetaKeyLabel()} + click to open card
              within milestone view
            </>
          ) : undefined
        }
        zoneInfo={zoneInfo}
      />
      <Box bold size={12} color="secondary" whiteSpace="nowrap">
        {ms.name}
      </Box>
    </>
  ) : (
    <CardPropChangeOverlayForChild
      cardId={cardId}
      cardContainerKey={`${cardContainerKey}-ms`}
      initialTab="milestone"
      disabled={!canModify}
      getChildProps={(open) => ({hidden: open ? true : undefined})}
    >
      <TooltipForChild
        tooltip={
          canModify && (
            <Box px="8px" py="4px">
              Set Milestone <DSShortcutKey>m</DSShortcutKey>
            </Box>
          )
        }
        delayed
      >
        <Box
          color="secondary"
          className={cx(
            uiClasses.hideElement,
            uiClasses.hideElementTargetOpacities["0.25"],
            uiClasses.hideElementInitialOpacities["0.15"]
          )}
        >
          <DSIconMilestone size={16} />
        </Box>
      </TooltipForChild>
    </CardPropChangeOverlayForChild>
  );
};

export const showMilestoneCell = (card: Card) =>
  process.env.REACT_APP_MODE !== "open" && card.$meta.get("milestone", null) !== null;

export const MsCell = (props: {
  card: Card;
  root: Root;
  cardContainerKey: string;
  canModify: boolean | null;
  zoneInfo?: string | null;
}) => {
  const {card, root, cardContainerKey, canModify, zoneInfo} = props;
  return (
    <Cell fixed={72} display="flex" sp="4px">
      <MsContent
        ms={card.milestone}
        cardId={card.cardId as CardId}
        root={root}
        cardContainerKey={cardContainerKey}
        canModify={canModify}
        zoneInfo={zoneInfo}
      />
    </Cell>
  );
};

export const showSprintCell = (card: Card) =>
  process.env.REACT_APP_MODE !== "open" && card.$meta.get("sprint", null) !== null;

const SprintContent = (props: {
  sprint: Sprint | null;
  card: Card;
  cardContainerKey: string;
  canModify: boolean | null;
  zoneInfo?: string | null;
}) => {
  const {sprint, card, cardContainerKey, canModify, zoneInfo} = props;
  if (process.env.REACT_APP_MODE === "open") return null;
  return sprint ? (
    <ThemedSprintIconWithTooltipAndMetaClick
      theme={setMsThemeColor(sprint.sprintConfig)}
      cardId={card.cardId as CardId}
      sprintId={sprint.id as SprintId}
      sprintState={getSprintState(sprint)}
      propChangeInfo={canModify && {cardId: card.cardId as CardId, cardContainerKey}}
      zoneInfo={zoneInfo}
      withShortcutHelp
    />
  ) : (
    <CardPropChangeOverlayForChild
      cardId={card.cardId as CardId}
      cardContainerKey={`${cardContainerKey}-sprint`}
      initialTab="sprint"
      disabled={!canModify}
      getChildProps={(open) => ({hidden: open ? true : undefined})}
    >
      <TooltipForChild
        tooltip={
          canModify && (
            <Box px="8px" py="4px">
              Set Run <DSShortcutKey>,</DSShortcutKey>
            </Box>
          )
        }
        delayed
      >
        <Box
          color="secondary"
          className={cx(
            uiClasses.hideElement,
            uiClasses.hideElementTargetOpacities["0.25"],
            uiClasses.hideElementInitialOpacities["0.15"]
          )}
        >
          <DSIconSprint size={16} />
        </Box>
      </TooltipForChild>
    </CardPropChangeOverlayForChild>
  );
};

export const SprintCell = (props: {
  card: Card;
  canModify: boolean | null;
  cardContainerKey: string;
  zoneInfo?: string | null;
}) => {
  const {card, canModify, cardContainerKey, zoneInfo} = props;
  return (
    <Cell fixed={16}>
      <SprintContent
        sprint={card.sprint}
        card={card}
        canModify={canModify}
        cardContainerKey={cardContainerKey}
        zoneInfo={zoneInfo}
      />
    </Cell>
  );
};

const DeckContent = ({deck}: {deck: Deck}) => (
  <>
    <DeckPickerImage deck={deck} />
    <Text type="label11" color="secondary">
      {deck.title}
    </Text>
  </>
);

export const DeckCell = (props: {
  card: Card;
  canModify: boolean | null;
  cardContainerKey: string;
}) => {
  const {card, canModify, cardContainerKey} = props;

  return (
    <CardPropChangeOverlayForChild
      cardId={card.cardId as CardId}
      cardContainerKey={`${cardContainerKey}-deck`}
      initialTab="deck"
      disabled={!canModify}
      getChildProps={(open) => ({hidden: open ? true : undefined})}
    >
      <TooltipForChild
        tooltip={
          canModify && (
            <Box px="8px" py="4px">
              Set Deck <DSShortcutKey>d</DSShortcutKey>
            </Box>
          )
        }
        delayed
      >
        <Cell fixed={72} flex={0.25} display="flex" sp="4px" align="center">
          {card.deck ? (
            <DeckContent deck={card.deck} />
          ) : (
            <>
              <DSIconGhost size={16} />
              <Box color="secondary" size={11} whiteSpace="nowrap">
                No deck
              </Box>
            </>
          )}
        </Cell>
      </TooltipForChild>
    </CardPropChangeOverlayForChild>
  );
};

const TimeTrackedContentCell = ({totalTime, cardId}: {totalTime: any; cardId: CardId}) => (
  <TooltipForChild tooltip={<TrackingTooltip cardId={cardId} />}>
    <Cell fixed={64} display="flex" sp="4px" align="center">
      <StopWatchIconWithProgress
        size={20}
        progress={trackedTimeToProgress(totalTime)}
        className={css({color: "primary"})}
      />
      <Box color="secondary" size={11} whiteSpace="nowrap">
        {formatMsAsMin(totalTime)}
      </Box>
    </Cell>
  </TooltipForChild>
);

export const showTimeTrackingCell = (card: Card, root: Root) => {
  return (
    process.env.REACT_APP_MODE !== "open" &&
    root.account.timeTrackingMode !== "none" &&
    getTotalTimeForCard(card) > 0
  );
};

export const TimeTrackedCell = ({card, root}: {card: Card; root: Root}) => {
  const totalTrackingTime =
    process.env.REACT_APP_MODE !== "open" &&
    root.account.timeTrackingMode !== "none" &&
    getTotalTimeForCard(card);
  return totalTrackingTime ? (
    <TimeTrackedContentCell totalTime={totalTrackingTime} cardId={card.cardId as CardId} />
  ) : (
    <Cell fixed={64} />
  );
};

const DueDateContentCell = ({dueDateInfo}: {dueDateInfo: DueDateInfo}) => {
  const {dueDate, dueDatesEnabled, workDays} = dueDateInfo;

  return (
    <TooltipForChild
      tooltip={() =>
        dueDatesEnabled ? (
          <DueDateTooltipContent dueDate={dueDate} workDays={workDays} />
        ) : (
          "This card has a disabled due date. Upgrade to the Pro plan to use this feature."
        )
      }
    >
      <Cell fixed={64} display="flex" sp="8px" align="center">
        <DueDateIcon {...({dueDatesEnabled, workDays} as any)} />
        <Box color="secondary" size={11} whiteSpace="nowrap" textOverflow="clip" bold>
          {workDays}d
        </Box>
      </Cell>
    </TooltipForChild>
  );
};

export const showDueDateCell = (card: Card, root: Root) =>
  Boolean(getDueDateInfo({card, account: root.account}));

export const DueDateCell = ({card, root}: {card: Card; root: Root}) => {
  const dueDateInfo = getDueDateInfo({card, account: root.account});
  return dueDateInfo ? <DueDateContentCell dueDateInfo={dueDateInfo} /> : <Cell fixed={64} />;
};

const UpvoteContentCell = ({
  upvoteInfo,
  card,
}: {
  upvoteInfo: ReturnType<typeof getUpvoteInfo>;
  card: Card;
}) => {
  const {myUpvoteId, totalCount} = upvoteInfo;

  return (
    <TooltipForChild
      tooltip={() => <UpvoteTooltip card={card} upvoteInfo={upvoteInfo} />}
      bg="gray700"
    >
      <Cell fixed={48} display="flex" sp="4px" align="center">
        <PotionIcon
          size={16}
          active={Boolean(myUpvoteId)}
          progress={Math.min(1, totalCount / 20)}
        />
        <Box color="secondary" size={11} whiteSpace="nowrap" textOverflow="clip" bold>
          {totalCount}
        </Box>
      </Cell>
    </TooltipForChild>
  );
};

export const showUpvotesCell = (card: Card, root: Root) => {
  const upvoteInfo = getUpvoteInfo({card, root});
  return Boolean(upvoteInfo?.totalCount);
};

export const UpvotesCell = ({card, root}: {card: Card; root: Root}) => {
  const upvoteInfo = getUpvoteInfo({card, root});
  return upvoteInfo?.totalCount ? (
    <UpvoteContentCell card={card} upvoteInfo={upvoteInfo} />
  ) : (
    <Cell fixed={48} />
  );
};

const ageSteps = {fresh: 3600, medium: 43200};
const agesAgo = new Date(0);

const getLastUpdateTime = (card: Card) => {
  const lastEntry = card.$meta.first("resolvableEntries", {$order: "-lastChangedAt"});
  const lastEntryChanged = lastEntry && lastEntry.$meta.get("lastChangedAt", agesAgo);
  const lastUpdatedAt = card.$meta.get("lastUpdatedAt", agesAgo);
  return lastEntryChanged && lastEntryChanged > lastUpdatedAt ? lastEntryChanged : lastUpdatedAt;
};

export const getForceShowMiscCell = (card: Card, root: Root) => {
  const visionBoardSeq = card.visionBoard?.$meta.get("accountSeq", null) ?? null;
  if (visionBoardSeq) return true;
  const beastLevel = getBeastLevel(root.account, card);
  if (beastLevel > 0) return true;
  const isNewUntil = getIsNewUntil(root.account, card);
  return isNewUntil !== null;
};

export const MiscCell = ({
  card,
  bag,
  leftOverWidth,
  zoneInfo,
  shownManualOrder,
}: {
  card: Card;
  bag: CardPanelBag;
  leftOverWidth: number;
  zoneInfo?: {sprint: string | null; milestone: string | null; deck: string | null} | null;
  shownManualOrder: string | null;
}) => {
  const {root, arenaCtx} = bag;
  if (leftOverWidth < -500) return null;

  const beastLevel = getBeastLevel(root.account, card);
  const isNewUntil = getIsNewUntil(root.account, card);

  const beastPillContent =
    beastLevel > 0 ? (
      <BeastPill level={beastLevel} isDone={card.status === "done"} isNewUntil={isNewUntil} />
    ) : isNewUntil ? (
      <AddedDuringSprintPill isDone={card.status === "done"} isNewUntil={isNewUntil} />
    ) : null;

  const visionBoardSeq = card.visionBoard?.$meta.get("accountSeq", null) ?? null;
  const visionBoardContent = visionBoardSeq && (
    <TooltipForChild tooltip="Open Vision Board">
      <Link to={`?visionBoard=${visionBoardSeq}`} onClick={(e) => e.stopPropagation()}>
        <DSIconMisc size={20} />
      </Link>
    </TooltipForChild>
  );

  if (leftOverWidth < 36) {
    return (
      <Cell fixed={32} display="flex" justify="end" align="center" sp="4px">
        {visionBoardContent}
        {beastPillContent}
      </Cell>
    );
  }

  const handInfo = getCardHandInfo({card, root});
  const lastUpdateTime = getLastUpdateTime(card);
  const diffInSeconds = (new Date().getTime() - lastUpdateTime.getTime()) / 1000;
  const dependenciesEnabled = root.account.dependenciesEnabled;
  const attachmentCount = card.$meta.count("attachments");

  const checkZoneBag = {
    zoneInfo,
    shownManualOrder,
    milestoneId: arenaCtx.type !== "milestone" && card.milestone?.id,
    sprintId: arenaCtx.type !== "sprint" && card.sprint?.id,
  };

  const getZoneHintBag = () => ({
    cardId: card.cardId,
    zoneInfo,
    milestoneState: card.milestone ? getMilestoneState(root.account, card.milestone) : null,
    milestoneColor: card.milestone?.color,
    sprintState: card.sprint ? getSprintState(card.sprint) : null,
    sprintColor: card.sprint?.sprintConfig?.color,
  });

  return (
    <Cell fixed={64} flex={0.1} display="flex" justify="end" align="center" sp="4px">
      {shouldShowZoneHint(checkZoneBag) && <ZoneHint bag={getZoneHintBag()} />}
      {handInfo && (
        <HandPositionHint
          position={handInfo.position}
          type={handInfo.type}
          cardId={card.cardId as CardId}
        />
      )}
      {diffInSeconds < ageSteps.medium ? (
        <TooltipForChild tooltip={<HourGlassTooltip lastUpdateTime={lastUpdateTime} />}>
          {diffInSeconds < ageSteps.fresh ? (
            <Ui.Icon.HourglassFull
              className={cx(dsStyles.size[12], (uiClasses.color as any).active600)}
            />
          ) : (
            <Ui.Icon.HourglassHalf
              className={cx(dsStyles.size[12], (uiClasses.color as any).active300)}
            />
          )}
        </TooltipForChild>
      ) : null}
      {card.hasBlockingDeps && (
        <TooltipForChild
          tooltip={
            dependenciesEnabled ? (
              <DependencyOverlay {...({card} as any)} />
            ) : (
              "This card has a disabled dependency. Upgrade to the Pro plan to use this feature."
            )
          }
          delayed
          placement="left"
          {...(dependenciesEnabled && {bg: "gray700"})}
        >
          <IconGateClosed
            {...({size: "lg"} as any)}
            style={dependenciesEnabled ? null : {opacity: 0.5}}
            className={dsStyles.color.primary}
          />
        </TooltipForChild>
      )}
      {attachmentCount > 0 ? (
        <TooltipForChild
          tooltip={() => (
            <Col pa="12px" sp="12px">
              {card.attachments.map((att, idx) => (
                <DisplayFile key={att.$meta.get("id", idx)} attachment={att} file={att.file} />
              ))}
            </Col>
          )}
          delayed
          bg="gray600"
          placement="left"
        >
          <DSIconAttachment16
            size={16}
            className={dsStyles.color.secondary}
            onClick={() =>
              arenaCtx.routes.goToCard({cardId: card.cardId, targetPanel: "attachments"})
            }
          />
        </TooltipForChild>
      ) : null}
      {visionBoardContent}
      {beastPillContent}
    </Cell>
  );
};

export const TableViewConvoHint = ({card, bag}: {card: Card; bag: CardPanelBag}) => {
  const convoInfo = getConvoPreviewInfo({notifications: bag.notifications, card, root: bag.root});
  if (!convoInfo) return null;
  return (
    <Box absolute style={{right: -10}} zIndex={1}>
      <ConversationHint
        convoInfo={convoInfo}
        cardId={card.cardId as CardId}
        gotoPanel={(panel: string) =>
          bag.arenaCtx.routes.goToCard({cardId: card.cardId, targetPanel: panel})
        }
        size="sm"
      />
    </Box>
  );
};

export const preloadProjectTags = (project: Project | null) => {
  if (!project) return;
  const t = project.tags;
  if (t.length) {
    t[0].$meta.get("tag");
    t[0].$meta.get("color");
    t[0].$meta.get("emoji");
    t[0].$meta.get("description");
  }
};

export const preloadCardCellInfo = (card: Card, bag: CardPanelBag) => {
  const {root} = bag;
  getStatusForCard(card);
  getConvoPreviewInfo({notifications: bag.notifications, card, root});
  getCardHandInfo({card, root});
  getLastUpdateTime(card);
  card.$meta.count("attachments");
  card.$meta.get("hasBlockingDeps");
  getUpvoteInfo({card, root});
  getDueDateInfo({card, account: root.account});
  if (process.env.REACT_APP_MODE !== "open" && root.account.timeTrackingMode !== "none") {
    getTotalTimeForCard(card);
  }
  const deck = card.deck;
  if (deck) {
    deck.$meta.get("title");
    deck.project?.$meta.get("id");
    preloadProjectTags(deck.project);
  }
  const ms = card.milestone;
  if (ms) {
    ms.$meta.get("name");
    ms.$meta.get("date");
    ms.$meta.get("color");
  }
  const sprint = card.sprint;
  if (sprint) {
    sprint.$meta.get("name");
    sprint.sprintConfig.$meta.get("color");
    getSprintState(sprint);
  }
  card.$meta.get("masterTags");
  card.$meta.get("title");
  const p = getParentCard(card);
  if (p) {
    p.$meta.get("title");
    p.$meta.get("masterTags");
    preloadProjectTags(p.deck?.project ?? null);
  }
  getChildCardInfo(card, root.account);
  card.$meta.get("assignee");
  card.$meta.get("effort");
  card.$meta.get("isDoc");
  card.$meta.get("priority");
  card.$meta.get("accountSeq");
  card.$meta.get("checkboxStats");
  card.$meta.get("meta");
  getCardOrderInfo(card, root.account);
  card.visionBoard?.$meta.get("accountSeq");
};

export const SelectionBox = ({
  onClick,
  isSelecting,
  isSelected,
  hasWaited,
}: {
  onClick: MouseEventHandler;
  hasWaited: boolean;
  isSelecting: boolean;
  isSelected: boolean;
}) => {
  return (
    <TooltipForChild tooltip="Select card" delayed>
      <div
        onClick={onClick}
        data-cdx-clickable
        className={cx(
          styles.selectionBox.base,
          hasWaited || isSelecting ? styles.selectionBox.shown : null,
          isSelecting && styles.selectionBox.selecting,
          isSelected && styles.selectionBox.selected
        )}
      >
        {isSelected ? <DSIconSelectionClose size={24} /> : <DSIconSelectionEmpty size={24} />}
      </div>
    </TooltipForChild>
  );
};

export const StatusRect = ({
  status,
  isLoaded,
  card,
}: {
  status: string;
  isLoaded: boolean;
  card: Card;
}) => {
  const loadedStatus = isLoaded ? status : null;
  const prevStatusRef = useRef(loadedStatus);

  const [rectStyles, springApi] = useSpring(() => ({
    scaleX: 1,
    config: {...springConfigs.quick, precision: 0.25},
  }));

  useEffect(() => {
    if (prevStatusRef.current && prevStatusRef.current !== loadedStatus) {
      springApi.start(() => ({
        from: {scaleX: 1},
        to: [{scaleX: 2.5}, {scaleX: 1}],
      }));
    }
    prevStatusRef.current = loadedStatus;
  }, [loadedStatus, springApi]);
  return (
    <animated.div
      style={rectStyles}
      className={cx(
        styles.statusRect.base,
        card.visibility === "archived" && styles.statusRect.archivedStripes
      )}
    />
  );
};

export const MultiDragHint = ({count}: {count: number}) => (
  <Col
    width="20px"
    height="20px"
    rounded="full"
    colorTheme="active50"
    bg="foreground"
    color="primary"
    borderWidth={1}
    borderColor="default"
    align="center"
    justify="center"
    size={count < 10 ? 12 : 11}
    lineHeight="14px"
    bold
    absolute
    zIndex={2}
    style={{left: -14}}
  >
    {count}
  </Col>
);
